import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Select from 'react-select';

const ViewDatesheet = () => {
  const { id } = useParams();
  const location = useLocation();
  const examFromState = location.state?.exam;

  const [examData, setExamData] = useState(examFromState || null);
  const [isEditing, setIsEditing] = useState(false);
  const [teacherOptions, setTeacherOptions] = useState([]);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  // Helper to display date as MM/DD/YYYY
  const formatDateMMDDYYYY = (date) => {
    if (!date) return '';

    const d = new Date(date);

    if (isNaN(d.getTime())) return '';

    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const year = d.getFullYear();

    return `${month}/${day}/${year}`;
  };

  // Fetch exam and teachers
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const sessionId = localStorage.getItem('currentSessionId');

        if (!sessionId) {
          alert('Session ID is missing. Please log in again.');
          return;
        }

        // Fetch exam
        const examResponse = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/exams/getExam/${id}`,
          {
            params: { sessionId },
          }
        );

        console.log(
          '📦 Exam response from backend:',
          examResponse.data
        );

        console.log(
          '📚 Subjects:',
          examResponse.data.subjects
        );

        const formattedSubjects = (
          examResponse.data.subjects || []
        ).map((sub) => ({
          ...sub,
          examDate: sub.examDate
            ? new Date(sub.examDate).toISOString().split('T')[0]
            : '',
        }));

        setExamData({
          ...examResponse.data,
          subjects: formattedSubjects,
        });

        // Fetch teachers
        const teacherResponse = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/teachers/getTeachers`,
          {
            params: { sessionId },
          }
        );

        console.log(
          '👨‍🏫 Teachers response:',
          teacherResponse.data
        );

        const teachersArray = Array.isArray(teacherResponse.data)
          ? teacherResponse.data
          : [teacherResponse.data];

        const options = teachersArray
          .filter((teacher) => teacher?._id)
          .map((teacher) => ({
            value: teacher._id,
            label: `${teacher.firstName || ''} ${
              teacher.lastName || ''
            }`.trim(),
          }));

        setTeacherOptions(options);
      } catch (err) {
        console.error('Error loading datesheet data:', err);

        alert('Failed to load exam data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  if (loading && !examData) {
    return (
      <h2 className="text-center mt-10 text-gray-500">
        Loading...
      </h2>
    );
  }

  if (!examData) {
    return (
      <h2 className="text-center mt-10 text-red-500">
        Exam data not found
      </h2>
    );
  }

  // Handle subject field changes
  const handleChange = (index, field, value) => {
    const updatedSubjects = [...examData.subjects];

    updatedSubjects[index] = {
      ...updatedSubjects[index],
      [field]: value,
    };

    setExamData({
      ...examData,
      subjects: updatedSubjects,
    });
  };

  // Handle exam name change
  const handleExamNameChange = (e) => {
    setExamData({
      ...examData,
      examName: e.target.value,
    });
  };

  // Delete subject
  const handleDeleteSubject = (index) => {
    if (examData.subjects.length <= 1) {
      alert('At least one subject is required in the datesheet.');
      return;
    }

    const subjectName = examData.subjects[index]?.subjectName;

    const confirmDelete = window.confirm(
      `Are you sure you want to remove "${subjectName}" from this datesheet?`
    );

    if (!confirmDelete) return;

    const updatedSubjects = examData.subjects.filter(
      (_, subjectIndex) => subjectIndex !== index
    );

    setExamData({
      ...examData,
      subjects: updatedSubjects,
    });
  };

  // Save changes
  const handleSave = async () => {
    try {
      if (!examData.examName?.trim()) {
        alert('Please enter exam name.');
        return;
      }

      if (
        !examData.subjects ||
        examData.subjects.length === 0
      ) {
        alert('At least one subject is required.');
        return;
      }

      // Validate subjects
      const invalidSubject = examData.subjects.find(
        (sub) =>
          !sub.examDate ||
          !sub.startTime ||
          !sub.endTime ||
          !sub.invigilator ||
          !sub.room
      );

      if (invalidSubject) {
        alert(
          `Please fill all required fields for ${invalidSubject.subjectName}.`
        );
        return;
      }

      const sessionId =
        localStorage.getItem('currentSessionId');

      if (!sessionId) {
        alert('Session ID is missing. Please log in again.');
        return;
      }

      const payload = {
        examName: examData.examName,
        class: examData.class,
        sessionId: sessionId,

        subjects: examData.subjects.map((sub) => ({
          subjectName: sub.subjectName,
          examDate: new Date(sub.examDate),
          startTime: sub.startTime,
          endTime: sub.endTime,
          invigilator: sub.invigilator,
          notes: sub.notes || '',
          room: sub.room,
        })),
      };

      console.log('📤 Update payload:', payload);

      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/exams/updateExam/${examData._id}`,
        payload,
        {
          params: {
            sessionId: sessionId,
          },
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              'token'
            )}`,
          },
        }
      );

      console.log(
        '✅ Updated exam response:',
        response.data
      );

      // Format returned dates for input
      const formattedSubjects = (
        response.data.subjects || []
      ).map((sub) => ({
        ...sub,
        examDate: sub.examDate
          ? new Date(sub.examDate)
              .toISOString()
              .split('T')[0]
          : '',
      }));

      setExamData({
        ...response.data,
        subjects: formattedSubjects,
      });

      setIsEditing(false);

      alert('Datesheet updated successfully');

      navigate('/exam-datesheet');
    } catch (err) {
      console.error(
        '❌ Error updating datesheet:',
        err
      );

      console.error(
        'Backend error:',
        err.response?.data
      );

      alert(
        err.response?.data?.message ||
          'Failed to update datesheet'
      );
    }
  };

  // Cancel editing
  const handleCancel = async () => {
    try {
      const sessionId =
        localStorage.getItem('currentSessionId');

      if (!sessionId) {
        setIsEditing(false);
        return;
      }

      // Reload original data from backend
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/exams/getExam/${id}`,
        {
          params: { sessionId },
        }
      );

      const formattedSubjects = (
        response.data.subjects || []
      ).map((sub) => ({
        ...sub,
        examDate: sub.examDate
          ? new Date(sub.examDate)
              .toISOString()
              .split('T')[0]
          : '',
      }));

      setExamData({
        ...response.data,
        subjects: formattedSubjects,
      });

      setIsEditing(false);
    } catch (err) {
      console.error(
        'Error restoring datesheet:',
        err
      );

      setIsEditing(false);
    }
  };

  return (
    <div className="max-w-full mx-auto p-6 bg-white shadow-2xl rounded-lg mt-3">

      {/* HEADER */}
      <div className="flex justify-between items-center mb-6">

        <div className="flex-1">
          {isEditing ? (
            <div className="max-w-xl">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Exam Name
              </label>

              <input
                type="text"
                value={examData.examName || ''}
                onChange={handleExamNameChange}
                className="border border-gray-300 p-3 rounded-lg text-lg font-bold w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
          ) : (
            <h2 className="text-2xl font-bold text-center">
              Datesheet for {examData.class} -{' '}
              {examData.examName}
            </h2>
          )}
        </div>

        {/* ACTION BUTTONS */}
        <div className="ml-4">

          {isEditing ? (
            <div className="flex gap-2">

              <button
                onClick={handleSave}
                className="bg-green-500 hover:bg-green-600 text-white px-5 py-2 rounded-lg font-semibold"
              >
                Save
              </button>

              <button
                onClick={handleCancel}
                className="bg-gray-400 hover:bg-gray-500 text-white px-5 py-2 rounded-lg font-semibold"
              >
                Cancel
              </button>

            </div>
          ) : (
            <button
              onClick={() => setIsEditing(true)}
              className="bg-blue-500 hover:bg-blue-600 text-white px-5 py-2 rounded-lg font-semibold"
            >
              Edit
            </button>
          )}

        </div>
      </div>

      {/* DATE SHEET TABLE */}
      <div className="overflow-x-auto">

        <table className="min-w-full border border-gray-300 divide-y divide-gray-200">

          <thead className="bg-gray-200">

            <tr>

              <th className="border border-gray-300 px-4 py-3 text-center">
                Sr. No.
              </th>

              <th className="border border-gray-300 px-4 py-3 text-center">
                Subject
              </th>

              <th className="border border-gray-300 px-4 py-3 text-center">
                Date
              </th>

              <th className="border border-gray-300 px-4 py-3 text-center">
                Timing
              </th>

              <th className="border border-gray-300 px-4 py-3 text-center">
                Invigilator
              </th>

              <th className="border border-gray-300 px-4 py-3 text-center">
                Room
              </th>

              <th className="border border-gray-300 px-4 py-3 text-center">
                Notes
              </th>

              {isEditing && (
                <th className="border border-gray-300 px-4 py-3 text-center">
                  Action
                </th>
              )}

            </tr>

          </thead>

          <tbody className="bg-gray-100 divide-y divide-gray-200">

            {examData.subjects.map((sub, idx) => (

              <tr
                key={sub._id || idx}
                className="hover:bg-gray-50"
              >

                {/* SR NO */}
                <td className="border border-gray-300 px-4 py-3 text-center font-semibold">
                  {idx + 1}
                </td>

                {/* SUBJECT */}
                <td className="border border-gray-300 px-4 py-3 text-center font-semibold">
                  {sub.subjectName}
                </td>

                {/* DATE */}
                <td className="border border-gray-300 px-4 py-3 text-center">

                  {isEditing ? (
                    <input
                      type="date"
                      value={sub.examDate || ''}
                      onChange={(e) =>
                        handleChange(
                          idx,
                          'examDate',
                          e.target.value
                        )
                      }
                      className="border border-gray-300 rounded px-2 py-2"
                    />
                  ) : (
                    formatDateMMDDYYYY(
                      sub.examDate
                    )
                  )}

                </td>

                {/* TIMING */}
                <td className="border border-gray-300 px-4 py-3 text-center">

                  {isEditing ? (

                    <div className="flex items-center justify-center gap-1">

                      <input
                        type="text"
                        value={sub.startTime || ''}
                        onChange={(e) =>
                          handleChange(
                            idx,
                            'startTime',
                            e.target.value
                          )
                        }
                        className="border border-gray-300 rounded px-2 py-2 w-24"
                        placeholder="Start"
                      />

                      <span>-</span>

                      <input
                        type="text"
                        value={sub.endTime || ''}
                        onChange={(e) =>
                          handleChange(
                            idx,
                            'endTime',
                            e.target.value
                          )
                        }
                        className="border border-gray-300 rounded px-2 py-2 w-24"
                        placeholder="End"
                      />

                    </div>

                  ) : (
                    `${sub.startTime} - ${sub.endTime}`
                  )}

                </td>

                {/* INVIGILATOR */}
                <td className="border border-gray-300 px-4 py-3 text-center">

                  {isEditing ? (

                    <div className="min-w-[220px] text-left">

                      <Select
                        options={teacherOptions}
                        value={
                          teacherOptions.find(
                            (option) =>
                              option.value ===
                              sub.invigilator
                          ) || null
                        }
                        onChange={(selectedOption) =>
                          handleChange(
                            idx,
                            'invigilator',
                            selectedOption
                              ? selectedOption.value
                              : ''
                          )
                        }
                        placeholder="Select Invigilator"
                        isClearable
                        styles={{
                          control: (base) => ({
                            ...base,
                            minHeight: '42px',
                            fontSize: '14px',
                          }),
                        }}
                      />

                    </div>

                  ) : (
                    sub.invigilatorName ||
                    teacherOptions.find(
                      (teacher) =>
                        teacher.value ===
                        sub.invigilator
                    )?.label ||
                    '-'
                  )}

                </td>

                {/* ROOM */}
                <td className="border border-gray-300 px-4 py-3 text-center">

                  {isEditing ? (

                    <input
                      type="text"
                      value={sub.room || ''}
                      onChange={(e) =>
                        handleChange(
                          idx,
                          'room',
                          e.target.value
                        )
                      }
                      className="border border-gray-300 rounded px-2 py-2 w-28"
                      placeholder="Room"
                    />

                  ) : (
                    sub.room || '-'
                  )}

                </td>

                {/* NOTES */}
                <td className="border border-gray-300 px-4 py-3 text-center">

                  {isEditing ? (

                    <input
                      type="text"
                      value={sub.notes || ''}
                      onChange={(e) =>
                        handleChange(
                          idx,
                          'notes',
                          e.target.value
                        )
                      }
                      className="border border-gray-300 rounded px-2 py-2 w-full min-w-[150px]"
                      placeholder="Notes"
                    />

                  ) : (
                    sub.notes || '-'
                  )}

                </td>

                {/* DELETE SUBJECT */}
                {isEditing && (
                  <td className="border border-gray-300 px-4 py-3 text-center">

                    <button
                      type="button"
                      onClick={() =>
                        handleDeleteSubject(idx)
                      }
                      className="bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded-lg font-semibold"
                    >
                      Delete
                    </button>

                  </td>
                )}

              </tr>

            ))}

          </tbody>

        </table>

      </div>

      {/* NO SUBJECT MESSAGE */}
      {examData.subjects.length === 0 && (
        <div className="text-center py-10 text-red-500 font-semibold">
          No subjects available in this datesheet.
        </div>
      )}

    </div>
  );
};

export default ViewDatesheet;